'use client'

import { AnimatePresence, motion } from 'framer-motion'
import Image from 'next/image'
import { usePathname } from 'next/navigation'
import { useState } from 'react'

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { EmailVerifyForm } from '@/features/auth/components/form/account-form'
import { AuthForm } from '@/features/auth/components/form/auth-form'
import { useAuthDialogControl } from '@/features/auth/hooks'
import { cn } from '@/lib/utils'

type DialogMode = 'signup' | 'login' | 'email-verify'

const AUTH_ROUTES = ['/login', '/register', '/email-verify', '/mfa', '/password']

export default function SignupDialog() {
  const [mode, setMode] = useState<DialogMode>('login')
  const pathname = usePathname()
  const { handleSuccess, closeDialog, isOpen } = useAuthDialogControl()

  const titleMap: Record<DialogMode, string> = {
    signup: 'Sign up',
    login: 'Log in',
    'email-verify': 'Confirm email',
  }

  if (AUTH_ROUTES.some((route) => pathname.startsWith(route))) return null

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeDialog()}>
      <DialogContent className="bg-background max-w-240 overflow-hidden p-0">
        <DialogHeader className="sr-only">
          <DialogTitle>Meet your personal AI Trading Team.</DialogTitle>
        </DialogHeader>
        <div className="flex h-full w-full items-stretch justify-stretch">
          <Image
            src="/images/auth/meet-the-team.webp"
            alt="auth-hero"
            className="hidden aspect-480/600 h-full flex-1 sm:flex"
            width={480}
            height={600}
          />
          <div className="my-auto flex flex-1 flex-col gap-7 overflow-hidden">
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={mode}
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.2, ease: 'easeInOut' }}
                className="flex flex-col gap-7"
              >
                <p className="mr-auto pl-5 text-start text-xl font-medium tracking-tight">
                  {titleMap[mode]}
                </p>
                {mode === 'email-verify' ? (
                  <EmailVerifyForm />
                ) : (
                  <AuthForm
                    className={cn([mode === 'login' && 'gap-2 py-0 pt-6'])}
                    mode={mode}
                    onSwitchMode={() => setMode(mode === 'signup' ? 'login' : 'signup')}
                    onEmailVerifyNeeded={() => setMode('email-verify')}
                  />
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
